// A finger/stylus signature pad. Strokes are drawn on a canvas (sized to the device pixel ratio so
// they stay crisp) and reported as a PNG data URL on each stroke end. touch-action: none keeps a
// finger drag drawing instead of scrolling the page.
//
// Extracted unchanged from JobDetail.tsx in Session AGG so the training screen captures an attendee
// signature with the same proven component the job sheet uses for the customer signature, rather
// than a second canvas implementation drifting beside it. The only addition is `placeholder`: the
// training screen signs several people in a row, so each pad says whose signature it is.
import { useEffect, useRef, useState } from "react";

export function SignaturePad({
  onChange,
  placeholder,
  clearLabel = "Clear signature",
}: {
  onChange: (dataUrl: string | null) => void;
  // Hint text shown over the empty pad. It disappears on the first stroke.
  placeholder?: string;
  clearLabel?: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const dirty = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.max(1, Math.round(rect.width * ratio));
    canvas.height = Math.max(1, Math.round(rect.height * ratio));
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#050816"; // dark ink (the pad background is white)
    }
  }, []);

  function pointAt(e: React.PointerEvent<HTMLCanvasElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = true;
    last.current = pointAt(e);
    setSigned(true);
  }

  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    const ctx = canvasRef.current?.getContext("2d");
    const p = pointAt(e);
    if (ctx && last.current) {
      ctx.beginPath();
      ctx.moveTo(last.current.x, last.current.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      dirty.current = true;
    }
    last.current = p;
  }

  function onPointerUp() {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;
    if (dirty.current && canvasRef.current) onChange(canvasRef.current.toDataURL("image/png"));
  }

  function clear() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    dirty.current = false;
    setSigned(false);
    onChange(null);
  }

  return (
    <div>
      <div className="relative">
        <canvas
          ref={canvasRef}
          className="h-40 w-full touch-none rounded-ele border border-ele-field-border bg-white"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        />
        {placeholder && !signed && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm"
            style={{ color: "#6b6b6b" }}
          >
            {placeholder}
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={clear}
        className="mt-1 flex min-h-11 items-center font-menu text-xs tracking-wider text-ele-green"
      >
        {clearLabel}
      </button>
    </div>
  );
}
